import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Navbar,
  SecondaryNavbar,
  FooterB,
  SelectorMenu,
} from "../../components";
import clsx from "clsx";
import * as turf from "@turf/turf";
import { TrendingDown, LineChart } from "lucide-react";
import { CityName } from "../../data/precipitation";
import TanzaniaMapPage, {
  getCityOption,
} from "./ClimateProfile/TanzaniaMapPage";
import {
  fetchZoneData,
  fetchProjections,
  fetchHighRiskAnalytics,
  fetchAdaptationOptionsAnalytics,
  fetchAllData,
  fetchHierarchicalAnalytics,
  fetchClimateZones,
  fetchOpenAIProxyNarrative,
} from "../../lib/utils";
import { useMutation } from "@tanstack/react-query";
import AIInterpretationDisplay, {
  AIInterpretationData,
} from "../../components/AIInterpretationDisplay";

function AdaptationOptionsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [zoneData, setZoneData] = useState<any>(null);
  useEffect(() => {
    let mounted = true;
    fetchZoneData()
      .then((res: any) => {
        if (!mounted) return;
        setZoneData(res);
      })
      .catch(() => setZoneData(null));
    return () => {
      mounted = false;
    };
  }, []);
  interface Product {
    id: string;
    label: string;
    value: string;
  }
  const [categories, setCategories] = useState<
    Array<{ id: number; name: string }>
  >([]);
  const [crops, setCrops] = useState<
    Array<{ id: number; name: string; category: number; category_name: string }>
  >([]);
  const [, setIsLoadingAdaptationMeta] = useState(false);
  const [periods, setPeriods] = useState<
    Array<{
      id: number;
      code: string;
      name: string;
      start_year: number;
      end_year: number;
    }>
  >([]);
  const [regions, setRegions] = useState<Array<{ id: number; name: string }>>(
    [],
  );
  const [scenarios, setScenarios] = useState<
    Array<{ id: number; code: string; name: string }>
  >([]);
  const [stressors, setStressors] = useState<
    Array<{ id: number; code: string; name: string }>
  >([]);
  const [climateZones, setClimateZones] = useState<
    Array<{ id: number; name: string; code?: string }>
  >([]);
  const [selectedScenario, setSelectedScenario] = useState<string>(
    searchParams.get("scenario") || "SSP245",
  );
  const [, setProjections] = useState<any[]>([]);
  const [highRisk, setHighRisk] = useState<any[]>([]);
  const [adaptationAnalytics, setAdaptationAnalytics] = useState<any[]>([]);
  const [selectedRegionId, setSelectedRegionId] = useState<number | null>(
    searchParams.get("region") ? Number(searchParams.get("region")) : null,
  );
  const [dominantStressors, setDominantStressors] = useState<
    Array<{
      stressor_code: string;
      stressor_name: string;
      rank: number;
    }>
  >([]);
  const [yieldChange, setYieldChange] = useState<number | null>(null);
  const [yieldImpact, setYieldImpact] = useState<string | null>(null);
  const [selectedCrop, setSelectedCrop] = useState<string>(
    searchParams.get("crop") || "Cereals",
  );
  const [availableProducts, setAvailableProducts] = useState<Product[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<string>(
    searchParams.get("product") || "Maize",
  );
  const [selectedDataTypeImpact, setSelectedDataTypeImpact] = useState(
    searchParams.get("dataType") || "yield_gap",
  );
  const [selectedDataPeriod, setSelectedDataPeriod] = useState<string>(
    searchParams.get("period") || "Near",
  );
  const [selectedZone, setSelectedZone] = useState<CityName>(
    (searchParams.get("zone") as CityName) || "arusha",
  );
  const [expandedRisk, setExpandedRisk] = useState<string | null>(null);
  const [selectedClimateZone, setSelectedClimateZone] = useState<string>(
    searchParams.get("climateZone") || "",
  );
  const [hierProjections, setHierProjections] = useState<any[]>([]);
  const [aiText, setAiText] = useState<string>("");
  const [aiData, setAiData] = useState<AIInterpretationData | null>(null);
  const [aiError, setAiError] = useState<string | null>(null);
  useEffect(() => {
    setIsLoadingAdaptationMeta(true);
    fetchAllData()
      .then((payload: any) => {
        const cats = payload?.crop_categories || [];
        const crps = payload?.crops || [];
        const prds = payload?.periods || [];
        const regs = payload?.regions || [];
        const scs = payload?.scenarios || [];
        const strs = payload?.stressors || [];

        setCategories(cats);
        setCrops(crps);
        setPeriods(prds);
        setRegions((regs || []).map((r: any) => ({ id: r.id, name: r.name })));
        setScenarios(scs);
        setStressors(strs);

        // initialize selectors respecting URL params
        // Crop
        const currentCrop = searchParams.get("crop") || selectedCrop;
        const isCropValid = (cats || []).some(
          (c: any) => c.name === currentCrop,
        );
        const effectiveCrop = isCropValid
          ? currentCrop
          : (cats?.[0]?.name ?? "");

        if (effectiveCrop !== selectedCrop) {
          setSelectedCrop(effectiveCrop);
        } else if (!isCropValid && cats.length > 0) {
          setSelectedCrop(cats[0].name);
        }

        // Products
        const productsForCrop = (crps || []).filter(
          (c: any) => c.category_name === effectiveCrop,
        );
        setAvailableProducts(
          productsForCrop.map((p: any) => ({
            id: String(p.id),
            label: p.name,
            value: p.name,
          })),
        );

        const currentProduct = searchParams.get("product") || selectedProduct;
        const isProductValid = productsForCrop.some(
          (p: any) => p.name === currentProduct,
        );
        if (!isProductValid && productsForCrop.length > 0) {
          setSelectedProduct(productsForCrop[0].name);
        }

        // Period
        const currentPeriod = searchParams.get("period") || selectedDataPeriod;
        const isPeriodValid = (prds || []).some(
          (p: any) => p.code === currentPeriod,
        );
        if (!isPeriodValid && prds.length > 0) {
          setSelectedDataPeriod(prds[0].code);
        }

        // Region & Zone
        const currentRegionId = searchParams.get("region")
          ? Number(searchParams.get("region"))
          : selectedRegionId;
        const isRegionValid = (regs || []).some(
          (r: any) => r.id === currentRegionId,
        );

        if (isRegionValid) {
          // Region is valid, check zone
          const currentZone = searchParams.get("zone") || selectedZone;
          // Ideally check if zone belongs to region, but we might not have that mapping easily here without more logic
          // For now just ensure zone is set.
          if (!currentZone && regs.length > 0) {
            // default to region name
            const reg = regs.find((r: any) => r.id === currentRegionId);
            if (reg) setSelectedZone(reg.name as any);
          }
        } else if (regs.length > 0) {
          setSelectedRegionId(regs[0].id);
          setSelectedZone((regs[0].name as any) ?? "arusha");
        }

        // Scenario
        const currentScenario =
          searchParams.get("scenario") || selectedScenario;
        const isScenarioValid = (scs || []).some(
          (s: any) => s.code === currentScenario,
        );
        if (!isScenarioValid && scs.length > 0) {
          setSelectedScenario(scs[0].code);
        }

        // Stressors
        const currentDataType =
          searchParams.get("dataType") || selectedDataTypeImpact;
        const isDataTypeValid = (strs || []).some(
          (s: any) => s.code === currentDataType,
        );
        // Note: DataType fallback handled elsewhere or below
        if (!isDataTypeValid && strs.length > 0) {
          setSelectedDataTypeImpact(strs[0].code);
        }
      })
      .catch((err) => {
        console.error("Error fetching adaptation meta data:", err);
      })
      .finally(() => setIsLoadingAdaptationMeta(false));
  }, []);

  // Sync state to URL
  useEffect(() => {
    const params: any = {};
    if (selectedCrop) params.crop = selectedCrop;
    if (selectedProduct) params.product = selectedProduct;
    if (selectedScenario) params.scenario = selectedScenario;
    if (selectedDataPeriod) params.period = selectedDataPeriod;
    if (selectedRegionId) params.region = String(selectedRegionId);
    if (selectedZone) params.zone = selectedZone;
    if (selectedClimateZone) params.climateZone = selectedClimateZone;
    if (selectedDataTypeImpact) params.dataType = selectedDataTypeImpact;

    setSearchParams(params, { replace: true });
  }, [
    selectedCrop,
    selectedProduct,
    selectedScenario,
    selectedDataPeriod,
    selectedRegionId,
    selectedZone,
    selectedClimateZone,
    selectedDataTypeImpact,
    setSearchParams,
  ]);

  // Charger la liste des zones climatiques pour le filtre optionnel
  useEffect(() => {
    fetchClimateZones()
      .then((cz) => setClimateZones(cz || []))
      .catch(() => setClimateZones([]));
  }, []);

  const cropsOptions = categories.map((cat) => ({
    id: String(cat.id),
    label: cat.name,
    value: cat.name,
  }));
  const dataTypeImpactOptions: Array<{
    id: string;
    label: string;
    value: string;
  }> = stressors.length
    ? stressors.map((s: { id: number; code: string; name: string }) => ({
        id: String(s.id),
        label: s.name,
        value: s.code,
      }))
    : [
        { id: "yield_gap", label: "Yield gap", value: "yield_gap" },
        { id: "cwd", label: "Crop Water Demand", value: "cwd" },
      ];
  const dataPeriodOptions = periods.length
    ? periods.map((p) => ({
        id: String(p.id),
        label: `${p.name} (${p.start_year}-${p.end_year})`,
        value: p.code,
      }))
    : [
        { id: "Near", label: "Near-term (2026-2055)", value: "Near" },
        { id: "Mid", label: "Mid-century (2041-2070)", value: "Mid" },
        { id: "Far", label: "End-century (2071-2100)", value: "Far" },
      ];

  const buildAIContext = (): string => {
    const zoneName = getCityOption(selectedZone)?.name ?? selectedZone;
    const productLabel =
      availableProducts.find((p) => p.value === selectedProduct)?.label ??
      selectedProduct;
    const periodLabel =
      dataPeriodOptions.find((p) => p.value === selectedDataPeriod)?.label ??
      selectedDataPeriod;
    const risksData = (highRisk || []).filter((r: any) => {
      const cropMatch =
        (r.crop || r.crop__name || "").toLowerCase() ===
        (selectedProduct || "").toLowerCase();
      const scenMatch =
        (r.scenario || r.scenario__code || "").toLowerCase() ===
        (selectedScenario || "").toLowerCase();
      const periodMatch =
        (r.period || r.period__code || "").toLowerCase() ===
        (selectedDataPeriod || "").toLowerCase();
      return cropMatch && scenMatch && periodMatch;
    });
    const risks = (risksData.length ? risksData : highRisk)
      .slice(0, 6)
      .map((r: any) => ({
        category: r.risk_category,
        risk_change: r.risk_change,
        yield_change: r.yield_change,
        risk_severity: r.risk_severity || r.severity, // Ensure this property is mapped
      }));
    const adaptationText = adaptationAnalytics?.[0]?.adaptation_options || "";
    const yi = yieldImpact || "N/A";
    const yc =
      typeof yieldChange === "number" ? `${yieldChange.toFixed(2)}%` : "N/A";

    // Summary variable removed as it was unused
    // const summary = [ ... ].join("; ");

    // Extract primary risk data for mobile-like prompt structure
    const primaryRisk = risks[0] || {};
    const riskCategory = primaryRisk.category || "Not specified";
    const riskSeverity = primaryRisk.risk_severity || "N/A"; // Fixed: removed .severity fallback if not present on type
    const riskChange =
      primaryRisk.risk_change !== undefined
        ? `${primaryRisk.risk_change > 0 ? "+" : ""}${primaryRisk.risk_change}`
        : "N/A";

    return [
      "You are an agricultural and climate expert in Tanzania.",
      "Your mission is to explain climate risks and impacts to farmers in a clear, actionable manner using simple language.",
      "Respond in English. Do not use markdown formatting (no **, __, *, _).",
      "",
      "--- INPUT DATA ---",
      `Crop: ${productLabel}`,
      `Region: ${zoneName}`,
      `Scenario: ${selectedScenario}`,
      `Period: ${periodLabel}`,
      "",
      "RISK DATA:",
      `- Risk Category: ${riskCategory}`,
      `- Severity: ${riskSeverity}/5`,
      `- Risk Change: ${riskChange}`,
      `(Consider other identified risks: ${JSON.stringify(risks.slice(1))})`,
      "",
      "IMPACT DATA:",
      `- Impact Type: ${yi}`,
      `- Yield Change: ${yc}`,
      "",
      "ADAPTATION CONTEXT:",
      `- Specific options available: ${adaptationText ? "Yes" : "No"}`,
      `- Options: ${adaptationText}`,
      "------------------",
      "",
      "--- OUTPUT FORMAT ---",
      "You MUST return a valid JSON object with the following structure:",
      "{",
      '  "risk_level": "Determine the overall risk level based on severity and risk change. Return exactly one of: LOW, MODERATE, HIGH, or SEVERE.",',
      '  "impact_level": "Determine the yield impact nature. Return exactly one of: NEGATIVE, NEUTRAL, POSITIVE, or UNCERTAIN.",',
      '  "risks": [',
      '    "Provide 2-3 concise bullet points about the risks for a farmer.",',
      '    "Focus on what this means for growing the crop in this region."',
      "  ],",
      '  "impact": "Provide 2-3 concise bullet points explaining what the yield change means for production with practical perspectives. Use a bullet point (•) for each line.",',
      '  "adaptation": [',
      '    "List the specific adaptation options provided in the input.",',
      '    "If no specific options are provided, give general guidance consulting experts."',
      "  ]",
      "}",
      "",
      "--- CONSTRAINTS ---",
      "1. Return ONLY the JSON object. Do not include markdown code blocks or extra text.",
      "2. Ensure the JSON is strictly valid (escape quotes, no trailing commas).",
      "3. Do not use real newlines in strings. Use \\n for line breaks if needed.",
      "4. Use professional, accessible language.",
      "5. Focus strictly on 'Risks', 'Impact', and 'Adaptation'.",
    ].join("\n");
  };

  const aiMutation = useMutation({
    mutationFn: async (ctx: string) => {
      // Use gpt-4o for better JSON adherence
      return await fetchOpenAIProxyNarrative(ctx, "gpt-4o");
    },
    onSuccess: (resp: any) => {
      const text =
        typeof resp?.data === "string" ? resp.data : String(resp?.data || "");
      console.log("[Narrative][AI] raw response", text);

      try {
        // Remove markdown formatting if present
        let jsonStr = text.replace(/```json\n?|\n?```/g, "").trim();

        // Robustness: try to find the first '{' and last '}'
        const firstBrace = jsonStr.indexOf("{");
        const lastBrace = jsonStr.lastIndexOf("}");
        if (firstBrace !== -1 && lastBrace !== -1) {
          jsonStr = jsonStr.substring(firstBrace, lastBrace + 1);
        }

        // Attempt to parse
        const data = JSON.parse(jsonStr);
        setAiData(data);
        setAiText("");
      } catch (e) {
        console.warn(
          "Failed to parse AI JSON response, attempting regex fallback",
          e,
        );

        // Regex Fallback to extract partial data
        try {
          const risksMatch = text.match(/"risks"\s*:\s*\[([\s\S]*?)\]/);
          const impactMatch = text.match(/"impact"\s*:\s*"([\s\S]*?)"/);
          const adaptationMatch = text.match(
            /"adaptation"\s*:\s*\[([\s\S]*?)\]/,
          );
          const riskLevelMatch = text.match(/"risk_level"\s*:\s*"([^"]+)"/);
          const impactLevelMatch = text.match(/"impact_level"\s*:\s*"([^"]+)"/);

          if (risksMatch || impactMatch || adaptationMatch) {
            const fallbackData: any = {};

            if (riskLevelMatch) {
              fallbackData.risk_level = riskLevelMatch[1];
            }
            if (impactLevelMatch) {
              fallbackData.impact_level = impactLevelMatch[1];
            }

            if (risksMatch) {
              // Naive parse of array content: split by "," and clean quotes
              fallbackData.risks = risksMatch[1]
                .split('",')
                .map((s: string) => s.replace(/['"]+/g, "").trim())
                .filter((s: string) => s.length > 0);
            }

            if (impactMatch) {
              fallbackData.impact = impactMatch[1];
            }

            if (adaptationMatch) {
              fallbackData.adaptation = adaptationMatch[1]
                .split('",')
                .map((s: string) => s.replace(/['"]+/g, "").trim())
                .filter((s: string) => s.length > 0);
            }

            console.log("Regex fallback successful", fallbackData);
            setAiData(fallbackData);
            setAiText("");
            setAiError(null);
            return;
          }
        } catch (regexError) {
          console.warn("Regex fallback failed", regexError);
        }

        setAiData(null);
        setAiText(text);
      }
      setAiError(null);
    },
    onError: () => {
      const fallback = buildLocalNarrative();
      console.log("[Narrative][Fallback]", fallback);
      setAiText(fallback);
      setAiData(null);
      setAiError(null);
    },
  });

  const buildLocalNarrative = (): string => {
    const zoneName = getCityOption(selectedZone)?.name ?? selectedZone;
    const productLabel =
      availableProducts.find((p) => p.value === selectedProduct)?.label ??
      selectedProduct;
    const periodLabel =
      dataPeriodOptions.find((p) => p.value === selectedDataPeriod)?.label ??
      selectedDataPeriod;
    const risksData = (highRisk || []).filter((r: any) => {
      const cropMatch =
        (r.crop || r.crop__name || "").toLowerCase() ===
        (selectedProduct || "").toLowerCase();
      const scenMatch =
        (r.scenario || r.scenario__code || "").toLowerCase() ===
        (selectedScenario || "").toLowerCase();
      const periodMatch =
        (r.period || r.period__code || "").toLowerCase() ===
        (selectedDataPeriod || "").toLowerCase();
      return cropMatch && scenMatch && periodMatch;
    });
    const risks = (risksData.length ? risksData : highRisk).slice(0, 6);
    const hasYieldChange = typeof yieldChange === "number";
    const ycLabel = hasYieldChange ? `${yieldChange.toFixed(2)}%` : "";
    const stressorsList = (dominantStressors || []).map((s) => s.stressor_name);
    const recs = (adaptationAnalytics?.[0]?.adaptation_options || "")
      .split(/;\s*/)
      .filter(Boolean);
    const overviewPara = [
      `This assessment focuses on the ${zoneName} region and examines ${productLabel} production.`,
      `The analysis uses the ${selectedScenario} climate scenario and covers the ${periodLabel} period.`,
    ].join("\n");
    const validRisks = risks.filter(
      (r: any) =>
        r &&
        (r.risk_category ||
          typeof r.risk_change === "number" ||
          typeof r.yield_change === "number"),
    );
    const rcVals = validRisks
      .map((r: any) => r.risk_change)
      .filter((v: any) => typeof v === "number");
    const avg = (arr: number[]) =>
      arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null;
    const avgRisk = avg(rcVals);

    let riskLevel = "MODERATE";
    if (avgRisk !== null) {
      if (avgRisk < 25) riskLevel = "LOW";
      else if (avgRisk < 50) riskLevel = "MODERATE";
      else if (avgRisk < 75) riskLevel = "HIGH";
      else riskLevel = "SEVERE";
    }

    const stressorBullets = stressorsList.length
      ? [
          `- More frequent or prolonged droughts`,
          `- Intense rainfall over short periods (flooding, erosion, runoff)`,
          `- Combined heat and water stress during critical growth stages`,
        ]
      : ["- Limited evidence of dominant climate stressors in available data"];
    const recsBullets = recs.length
      ? recs.map((r: string, i: number) => `${i + 1}. ${r}`)
      : [
          "1. Adaptation options are not available in the dataset for this selection",
        ];
    const impactPara = hasYieldChange
      ? `On average, yields are expected to ${ycLabel.startsWith("-") ? "decline" : "change"} by about ${ycLabel}. While this may appear modest, it can have meaningful consequences for smallholders.`
      : `The dataset does not provide a reliable estimate of average yield change for this selection; practical impacts may vary across seasons and locations.`;

    let impactLevel = "UNCERTAIN";
    if (hasYieldChange) {
      if (yieldChange < -5) impactLevel = "NEGATIVE";
      else if (yieldChange < 0) impactLevel = "SLIGHTLY NEGATIVE";
      else if (yieldChange === 0) impactLevel = "NEUTRAL";
      else impactLevel = "POSITIVE";
    }

    return [
      `## Risks (${riskLevel} Risk)`,
      validRisks.length
        ? `The analysis highlights notable climate risks with changing conditions. Average risk change: ${avgRisk !== null ? avgRisk.toFixed(2) : "N/A"}. Yield variability and uncertainty are likely to increase.`
        : `Risk details are limited for this selection; yields may still face variability and uncertainty due to climate stressors.`,
      stressorBullets.join("\n"),
      `## Impact (${impactLevel} Impact)`,
      overviewPara,
      impactPara,
      `## Adaptation`,
      recsBullets.join("\n"),
      `In ${zoneName}, ${productLabel} production is expected to face increasing climate stress and variability. By adopting resilient varieties, improving water and soil management, and strengthening post-harvest practices, farmers can reduce losses, stabilize yields, and build resilience.`,
    ].join("\n\n");
  };

  const handleAnalyze = () => {
    setAiError(null);
    setAiData(null);
    setAiText("");
    aiMutation.mutate(buildAIContext());
  };

  useEffect(() => {
    const key = [
      selectedZone,
      selectedProduct,
      selectedScenario,
      selectedDataPeriod,
      yieldImpact,
      yieldChange,
      (highRisk || []).length,
      (dominantStressors || []).length,
      adaptationAnalytics?.[0]?.adaptation_options || "",
    ].join("|");
    if (!aiMutation.isPending && key) {
      setAiText("");
      setAiData(null);
      aiMutation.mutate(buildAIContext());
    }
  }, [
    selectedZone,
    selectedProduct,
    selectedScenario,
    selectedDataPeriod,
    yieldImpact,
    yieldChange,
    highRisk,
    dominantStressors,
    adaptationAnalytics,
  ]);

  // Filtres visibles (inclut en permanence climate_zone)
  const [visibleFilters, setVisibleFilters] = useState<Array<string>>([
    "climate_zone",
    "region",
    "scenario",
    "period",
    "crop",
  ]);

  const toggleFilterVisibility = (key: string) => {
    // climate_zone reste toujours visible
    if (key === "climate_zone") return;
    setVisibleFilters((prev) => {
      const has = prev.includes(key);
      if (has) return prev.filter((k) => k !== key);
      return [...prev, key];
    });
  };

  // Ordre hiérarchique pour l'API (climate_zone en tête si activé)
  const getHierarchyString = (): string => {
    const order = ["climate_zone", "region", "scenario", "period", "crop"];
    const active = order.filter(
      (k) => k === "climate_zone" || visibleFilters.includes(k),
    );
    return active.join(",");
  };
  useEffect(() => {
    const availableCodes = dataTypeImpactOptions.map((o) => o.value);
    if (!availableCodes.includes(selectedDataTypeImpact)) {
      setSelectedDataTypeImpact(availableCodes[0] || "yield_gap");
    }
  }, [stressors]);

  useEffect(() => {
    const codes = periods.map((p) => p.code);
    if (!codes.includes(selectedDataPeriod)) {
      setSelectedDataPeriod(codes[0] || "Near");
    }
  }, [periods]);

  useEffect(() => {
    const products = crops
      .filter((c) => c.category_name === selectedCrop)
      .map((p) => ({ id: String(p.id), label: p.name, value: p.name }));
    setAvailableProducts(products);
    if (!products.find((p) => p.value === selectedProduct)) {
      setSelectedProduct(products[0]?.value || "");
    }
  }, [selectedCrop, crops]);

  useEffect(() => {
    const cropId = crops.find((c) => c.name === selectedProduct)?.id;
    if (
      !selectedRegionId ||
      !cropId ||
      !selectedScenario ||
      !selectedDataPeriod
    )
      return;
    const params = {
      region: selectedRegionId,
      crop: cropId,
      scenario: selectedScenario,
      period: selectedDataPeriod,
    };

    fetchProjections(params)
      .then((data) => {
        const list = data || [];
        setProjections(list);

        // Handle yield change and dominant stressors from the same response
        const yc = list?.[0]?.yield_change;
        setYieldChange(typeof yc === "number" ? yc : null);
        const dsArr = list?.[0]?.dominant_stressors;
        if (Array.isArray(dsArr) && dsArr.length) {
          setDominantStressors(
            (dsArr as any[]).map((s: any, idx: number) => ({
              stressor_code: String(s?.stressor_code ?? s),
              stressor_name: String(s?.stressor_name ?? s),
              rank: idx + 1,
            })),
          );
        }
      })
      .catch((err) => console.error("Error fetching projections:", err));

    fetchHighRiskAnalytics(params)
      .then((data) => setHighRisk(data || []))
      .catch((err) =>
        console.error("Error fetching high risk analytics:", err),
      );

    fetchAdaptationOptionsAnalytics(params)
      .then((data) => setAdaptationAnalytics(data || []))
      .catch((err) =>
        console.error("Error fetching adaptation analytics:", err),
      );

    fetchHierarchicalAnalytics({
      hierarchy: getHierarchyString(),
      include_projections: true,
    })
      .then((payload: any) => {
        const targetRegionName = (
          regions.find((r) => r.id === selectedRegionId)?.name || ""
        ).toLowerCase();
        const targetCrop = (selectedProduct || "").toLowerCase();
        const targetScenario = (selectedScenario || "").toLowerCase();
        const targetPeriod = (selectedDataPeriod || "").toLowerCase();
        const targetCZ = (selectedClimateZone || "").toLowerCase();

        const collectProjections = (node: any, acc: any[] = []): any[] => {
          if (!node) return acc;
          if (Array.isArray(node)) {
            node.forEach((n) => collectProjections(n, acc));
            return acc;
          }
          if (Array.isArray(node?.projections)) acc.push(...node.projections);
          Object.values(node).forEach((val: any) => {
            if (val && (typeof val === "object" || Array.isArray(val))) {
              collectProjections(val, acc);
            }
          });
          return acc;
        };

        const projections: any[] = collectProjections(payload?.data, []);
        setHierProjections(projections);
        const match = projections.find((p: any) => {
          const regionOk =
            (p.region_name || p.region || "").toLowerCase() ===
            targetRegionName;
          const cropOk =
            (p.crop_name || p.crop || "").toLowerCase() === targetCrop;
          const scenarioOk =
            (p.scenario_code || p.scenario || "").toLowerCase() ===
            targetScenario;
          const periodOk =
            (p.period_code || p.period || "").toLowerCase() === targetPeriod;
          const czField = (
            p.climate_zone ||
            p.climate_zone_code ||
            p.climate_zone_name ||
            ""
          ).toLowerCase();
          const czOk = !targetCZ || czField === targetCZ;
          return regionOk && cropOk && scenarioOk && periodOk && czOk;
        });

        if (match) {
          if (typeof match.yield_change === "number")
            setYieldChange(match.yield_change);
          if (match.yield_impact) setYieldImpact(match.yield_impact);
          if (
            Array.isArray(match.dominant_stressors) &&
            match.dominant_stressors.length
          ) {
            setDominantStressors(
              (match.dominant_stressors as string[]).map(
                (s: string, idx: number) => ({
                  stressor_code: String(s),
                  stressor_name: String(s),
                  rank: idx + 1,
                }),
              ),
            );
          }
        }
      })
      .catch((err) =>
        console.error("Error fetching hierarchical analytics:", err),
      );
  }, [
    selectedRegionId,
    selectedProduct,
    selectedScenario,
    selectedDataPeriod,
    crops,
    visibleFilters,
    selectedClimateZone,
  ]);

  // Options valides basées sur les projections
  const normalize = (v: any) => String(v ?? "").toLowerCase();
  const regionName = normalize(
    regions.find((r) => r.id === selectedRegionId)?.name || "",
  );
  const cropName = normalize(selectedProduct);
  const scenarioCode = normalize(selectedScenario);
  const periodCode = normalize(selectedDataPeriod);
  const climateZoneCodeOrName = normalize(selectedClimateZone);

  const matches = (
    p: any,
    opts: {
      region?: boolean;
      crop?: boolean;
      scenario?: boolean;
      period?: boolean;
      climate_zone?: boolean;
    },
  ) => {
    const regionOk =
      !opts.region || normalize(p.region_name || p.region) === regionName;
    const cropOk = !opts.crop || normalize(p.crop_name || p.crop) === cropName;
    const scenarioOk =
      !opts.scenario ||
      normalize(p.scenario_code || p.scenario) === scenarioCode;
    const periodOk =
      !opts.period || normalize(p.period_code || p.period) === periodCode;
    const czField = normalize(
      p.climate_zone || p.climate_zone_code || p.climate_zone_name,
    );
    const czOk =
      !opts.climate_zone ||
      !climateZoneCodeOrName ||
      czField === climateZoneCodeOrName;
    return regionOk && cropOk && scenarioOk && periodOk && czOk;
  };

  const unique = (arr: string[]) => Array.from(new Set(arr));

  const validProductNames = unique(
    hierProjections
      .filter((p) =>
        matches(p, {
          region: true,
          scenario: true,
          period: true,
          climate_zone: true,
        }),
      )
      .map((p) => String(p.crop_name || p.crop)),
  );
  const validProducts = crops
    .filter((c) => c.category_name === selectedCrop)
    .filter((c) => validProductNames.map(normalize).includes(normalize(c.name)))
    .map((p) => ({ id: String(p.id), label: p.name, value: p.name }));

  const validScenarioCodes = unique(
    hierProjections
      .filter((p) =>
        matches(p, {
          region: true,
          period: true,
          crop: true,
          climate_zone: true,
        }),
      )
      .map((p) => String(p.scenario_code || p.scenario)),
  );
  const validScenarios = scenarios.filter((s) =>
    validScenarioCodes.map(normalize).includes(normalize(s.code)),
  );

  const validPeriodCodes = unique(
    hierProjections
      .filter((p) =>
        matches(p, {
          region: true,
          scenario: true,
          crop: true,
          climate_zone: true,
        }),
      )
      .map((p) => String(p.period_code || p.period)),
  );
  const validPeriods = periods.filter((p) =>
    validPeriodCodes.map(normalize).includes(normalize(p.code)),
  );

  const validRegionNames = unique(
    hierProjections
      .filter((p) =>
        matches(p, {
          scenario: true,
          period: true,
          crop: true,
          climate_zone: true,
        }),
      )
      .map((p) => String(p.region_name || p.region)),
  );
  const validRegions = regions.filter((r) =>
    validRegionNames.map(normalize).includes(normalize(r.name)),
  );

  const validClimateZones = climateZones.filter((cz) => {
    const codeOrName = normalize(cz.code || cz.name);
    const inProjections = hierProjections
      .filter((p) =>
        matches(p, { region: true, scenario: true, period: true, crop: true }),
      )
      .some((p) => {
        const czField = normalize(
          p.climate_zone || p.climate_zone_code || p.climate_zone_name,
        );
        return !codeOrName || czField === codeOrName;
      });
    return inProjections;
  });

  // Auto-ajustement lorsque la sélection est invalide
  useEffect(() => {
    if (hierProjections.length === 0) return;
    if (
      validProducts.length &&
      !validProducts.find((p) => normalize(p.value) === cropName)
    ) {
      setSelectedProduct(validProducts[0].value);
      return;
    }
    if (
      validScenarios.length &&
      !validScenarios.find((s) => normalize(s.code) === scenarioCode)
    ) {
      setSelectedScenario(validScenarios[0].code);
      return;
    }
    if (
      validPeriods.length &&
      !validPeriods.find((p) => normalize(p.code) === periodCode)
    ) {
      setSelectedDataPeriod(validPeriods[0].code);
      return;
    }
    if (
      validRegions.length &&
      !validRegions.find((r) => r.id === selectedRegionId)
    ) {
      setSelectedRegionId(validRegions[0].id);
      const rn = validRegions[0]?.name;
      if (rn) setSelectedZone(rn as any);
      return;
    }
    if (
      validClimateZones.length &&
      selectedClimateZone &&
      !validClimateZones.find(
        (cz) => normalize(cz.code || cz.name) === climateZoneCodeOrName,
      )
    ) {
      setSelectedClimateZone(
        validClimateZones[0]?.code || validClimateZones[0]?.name || "",
      );
      return;
    }
  }, [
    hierProjections,
    selectedCrop,
    selectedRegionId,
    selectedScenario,
    selectedDataPeriod,
    selectedProduct,
    selectedClimateZone,
  ]);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <SecondaryNavbar />

      <main className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full flex flex-col flex-1">
        <header className="sticky top-0 bg-gray-50 z-20 mb-4">
          <h1 className="text-2xl font-bold text-gray-900">
            Adaptation Options
          </h1>
        </header>
        <div className=" border-gray-200  p-4  mb-6">
          {/* Contrôles de visibilité des filtres */}
          <div
            className="flex flex-wrap items-center gap-3 mb-3"
            style={{ display: "none" }}
          >
            <span className="text-sm font-medium text-gray-700">Filters:</span>
            {["region", "scenario", "period", "crop", "climate_zone"].map(
              (key) => (
                <label
                  key={key}
                  className="inline-flex items-center gap-2 text-sm text-gray-700"
                >
                  <input
                    type="checkbox"
                    className="rounded border-gray-300"
                    checked={visibleFilters.includes(key)}
                    onChange={() => toggleFilterVisibility(key)}
                  />
                  <span className="capitalize">{key.replace("_", " ")}</span>
                </label>
              ),
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 items-end pb-2">
            <SelectorMenu
              options={cropsOptions}
              value={selectedCrop}
              onChange={setSelectedCrop}
              label="Crops"
            />
            <SelectorMenu
              options={(validProducts.length
                ? validProducts
                : availableProducts
              ).map((prod) => ({
                id: prod.id,
                label: prod.label,
                value: prod.value,
              }))}
              value={selectedProduct}
              onChange={setSelectedProduct}
              label="Products"
            />
            {visibleFilters.includes("period") && (
              <SelectorMenu
                options={
                  validPeriods.length
                    ? validPeriods.map((p) => ({
                        id: String(p.id),
                        label: `${p.name} (${p.start_year}-${p.end_year})`,
                        value: p.code,
                      }))
                    : dataPeriodOptions
                }
                value={selectedDataPeriod}
                onChange={setSelectedDataPeriod}
                label="Period"
              />
            )}
            {visibleFilters.includes("region") && (
              <SelectorMenu
                options={(validRegions.length ? validRegions : regions).map(
                  (r) => ({
                    id: String(r.id),
                    label: r.name,
                    value: String(r.id),
                  }),
                )}
                value={selectedRegionId ? String(selectedRegionId) : ""}
                onChange={(val: string) => {
                  const rid = parseInt(val);
                  setSelectedRegionId(rid);
                  const rn = regions.find((r) => r.id === rid)?.name;
                  if (rn) setSelectedZone(rn as any);
                }}
                label="Region"
              />
            )}
            {visibleFilters.includes("scenario") && (
              <SelectorMenu
                options={(validScenarios.length
                  ? validScenarios
                  : scenarios
                ).map((s) => ({
                  id: String(s.id),
                  label: s.name || s.code,
                  value: s.code,
                }))}
                value={selectedScenario}
                onChange={setSelectedScenario}
                label="Scenario"
              />
            )}
            {visibleFilters.includes("climate_zone") &&
              climateZones.length > 0 && (
                <SelectorMenu
                  options={(validClimateZones.length
                    ? validClimateZones
                    : climateZones
                  ).map((cz) => ({
                    id: String(cz.id),
                    label: cz.name,
                    value: cz.code || cz.name,
                  }))}
                  value={selectedClimateZone}
                  onChange={setSelectedClimateZone}
                  label="Climate Zone"
                  className="hidden"
                />
              )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.5fr] gap-6 items-start">
          <div className="lg:sticky lg:top-28 space-y-4">
            <div className="bg-white h-[600px] border border-gray-300 rounded overflow-hidden ">
              <TanzaniaMapPage
                selectedCity={selectedZone}
                zoneData={zoneData}
                onCitySelect={(opt) => {
                  const city = opt.id as CityName;
                  setSelectedZone(city);
                  const rn = getCityOption(city)?.name ?? (city as string);
                  const rid = regions.find(
                    (r) => r.name.toLowerCase() === rn.toLowerCase(),
                  )?.id;
                  if (rid) setSelectedRegionId(rid);
                }}
              />
            </div>
          </div>

          <div className="space-y-6">
            {(() => {
              const periodLabel =
                dataPeriodOptions.find((p) => p.value === selectedDataPeriod)
                  ?.label ?? selectedDataPeriod;
              const climateDescription = `Climate adaptation insights`;
              return (
                <div
                  className={clsx(
                    "text-gray-800 space-y-6 whitespace-pre-wrap",
                    "bg-white p-6 rounded-lg shadow-md",
                  )}
                >
                  <h2 className="text-xl font-semibold">
                    {climateDescription}: Tanzania — {periodLabel}
                  </h2>
                  {(() => {
                    const zoneName =
                      getCityOption(selectedZone)?.name ?? selectedZone;
                    const zoneFeat = zoneData?.features?.find(
                      (f: any) =>
                        f?.properties?.ADM1_EN?.toLowerCase() ===
                        zoneName?.toLowerCase(),
                    );
                    const areaKm2 = zoneFeat
                      ? (turf.area(zoneFeat) / 1_000_000).toFixed(0)
                      : "N/A";
                    const coordsStr =
                      getCityOption(selectedZone)
                        ?.coords?.map((c: number) => Number(c).toFixed(2))
                        ?.join(", ") ?? "N/A";
                    const pinfo = periods.find(
                      (p) => p.code === selectedDataPeriod,
                    );
                    const yearRange = pinfo
                      ? `${pinfo.start_year}-${pinfo.end_year}`
                      : "YYYY-YYYY";
                    const periodLabel = pinfo ? pinfo.name : selectedDataPeriod;
                    return (
                      <div className="flex flex-nowrap items-center justify-between gap-6 text-sm text-gray-700 w-full">
                        <div>
                          <span className="font-semibold">Coordinates:</span>{" "}
                          {coordsStr}
                        </div>
                        <div>
                          <span className="font-semibold">Area:</span> {areaKm2}{" "}
                          km²
                        </div>
                        <div>
                          <span className="font-semibold">Period:</span>{" "}
                          {periodLabel} ({yearRange})
                        </div>
                      </div>
                    );
                  })()}
                  {/*
                  <div
                    className={`border-l-4 p-4 rounded bg-opacity-10`}
                    style={{
                      borderColor: textColor,
                      backgroundColor: `${textColor}11`,
                    }}
                  >
                    <p className="text-sm text-gray-600 font-bold">
                      Key Insights
                    </p>
                    <ReactMarkdown>{keyInsights}</ReactMarkdown>
                  </div>
                  */}

                  {/* <section className="mt-4"> */}
                  {/* <h3 className="text-lg font-semibold mb-2">Identified Climate Risks</h3> */}
                  {(() => {
                    const filtered = (highRisk || []).filter((r: any) => {
                      const cropMatch =
                        (r.crop || r.crop__name || "").toLowerCase() ===
                        (selectedProduct || "").toLowerCase();
                      const scenMatch =
                        (r.scenario || r.scenario__code || "").toLowerCase() ===
                        (selectedScenario || "").toLowerCase();
                      const periodMatch =
                        (r.period || r.period__code || "").toLowerCase() ===
                        (selectedDataPeriod || "").toLowerCase();
                      return cropMatch && scenMatch && periodMatch;
                    });
                    const base = (filtered.length ? filtered : highRisk).slice(
                      0,
                      4,
                    );
                    const risks = base.map((r: any, idx: number) => ({
                      id: `risk_${idx}`,
                      title: r.risk_category || "Risk",
                      impact: `Risk change: ${r.risk_change?.toFixed?.(2) ?? r.risk_change}; Yield change: ${r.yield_change?.toFixed?.(2) ?? r.yield_change}`,
                      severity: (r.risk_category || "")
                        .toLowerCase()
                        .includes("high")
                        ? "high"
                        : "medium",
                      color: (r.risk_category || "")
                        .toLowerCase()
                        .includes("high")
                        ? "#ef4444"
                        : "#f59e0b",
                      icon: (r.risk_category || "")
                        .toLowerCase()
                        .includes("high")
                        ? TrendingDown
                        : LineChart,
                    }));
                    return (
                      <div
                        style={{
                          display: "none",
                        }}
                      >
                        {risks.map((risk) => {
                          const Icon = risk.icon as any;
                          const isExpanded = expandedRisk === risk.id;
                          return (
                            <div
                              key={risk.id}
                              onClick={() =>
                                setExpandedRisk(isExpanded ? null : risk.id)
                              }
                              style={{
                                background: isExpanded
                                  ? "rgba(255, 255, 255, 0.08)"
                                  : "rgba(255, 255, 255, 0.03)",
                                border: `1px solid ${isExpanded ? risk.color + "40" : "rgba(0, 0, 0, 0.1)"}`,
                                borderRadius: "12px",
                                padding: "1.25rem",
                                cursor: "pointer",
                                transition:
                                  "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                                transform: isExpanded
                                  ? "translateY(-2px)"
                                  : "translateY(0)",
                                boxShadow: isExpanded
                                  ? `0 8px 24px ${risk.color}25`
                                  : "none",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "1rem",
                                    flex: 1,
                                  }}
                                >
                                  <div
                                    style={{
                                      width: "48px",
                                      height: "48px",
                                      background: `${risk.color}20`,
                                      borderRadius: "12px",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      border: `1px solid ${risk.color}40`,
                                    }}
                                  >
                                    <Icon
                                      style={{
                                        color: risk.color,
                                        width: "24px",
                                        height: "24px",
                                      }}
                                    />
                                  </div>
                                  <div style={{ flex: 1 }}>
                                    <h3
                                      style={{
                                        margin: "0 0 0.25rem 0",
                                        fontSize: "1rem",
                                        fontWeight: 600,
                                        color: "#111827",
                                      }}
                                    >
                                      {risk.title}
                                    </h3>
                                    <p
                                      style={{
                                        margin: 0,
                                        fontSize: "0.875rem",
                                        color: "#4b5563",
                                      }}
                                    >
                                      {risk.impact}
                                    </p>
                                  </div>
                                </div>
                                <div
                                  style={{
                                    padding: "0.375rem 0.875rem",
                                    background:
                                      risk.severity === "high"
                                        ? "rgba(255, 107, 107, 0.15)"
                                        : "rgba(255, 169, 77, 0.15)",
                                    borderRadius: "20px",
                                    fontSize: "0.75rem",
                                    fontWeight: 600,
                                    color:
                                      risk.severity === "high"
                                        ? "#FF6B6B"
                                        : "#FFA94D",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.05em",
                                  }}
                                >
                                  {risk.severity === "high" ? "High" : "Medium"}
                                </div>
                              </div>

                              {isExpanded && (
                                <div
                                  style={{
                                    marginTop: "1rem",
                                    paddingTop: "1rem",
                                    borderTop: "1px solid rgba(0, 0, 0, 0.1)",
                                  }}
                                >
                                  {(() => {
                                    const pinfo = periods.find(
                                      (p) => p.code === selectedDataPeriod,
                                    );
                                    const periodLabel = pinfo
                                      ? pinfo.name
                                      : selectedDataPeriod;
                                    const yc =
                                      yieldChange !== null
                                        ? `${yieldChange.toFixed(2)}%`
                                        : "N/A";
                                    return (
                                      <p
                                        style={{
                                          margin: 0,
                                          fontSize: "0.875rem",
                                          lineHeight: "1.6",
                                          color: "#374151",
                                        }}
                                      >
                                        Crop: {selectedProduct} • Region:{" "}
                                        {selectedZone} • Scenario:{" "}
                                        {selectedScenario} • Period:{" "}
                                        {periodLabel} • Yield change: {yc} •
                                        Risk: {risk.title}
                                      </p>
                                    );
                                  })()}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                  {/* </section> */}

                  {/* <section className="mt-4"> */}
                  {/* <h3 className="text-lg font-semibold mb-2">Dominant Stressors</h3> */}
                  <div
                    className="flex flex-col gap-2"
                    style={{ display: "none" }}
                  >
                    {(dominantStressors || []).length ? (
                      dominantStressors.map((s, idx) => (
                        <div
                          key={`${s.stressor_code}_${idx}`}
                          className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-xl p-3"
                        >
                          <div className="font-semibold text-gray-800">
                            {s.stressor_name}
                          </div>
                          <div className="text-sm text-gray-600">
                            Rank: {s.rank}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-sm text-gray-600">
                        No stressor data available for this selection.
                      </div>
                    )}
                  </div>
                  {/* </section> */}

                  <section className="mt-6">
                    {/*
                    {(() => {
                      const text =
                        adaptationAnalytics?.[0]?.adaptation_options ||
                        "No adaptation options available";
                      // Recommended adaptation options card commented
                      return null;
                    })()}
                    */}

                    {(() => {
                      return (
                        <div className="space-y-3">
                          <div className="flex items-center gap-3">
                            <button
                              onClick={handleAnalyze}
                              className="px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700"
                            >
                              Analyze with AI
                            </button>
                          </div>
                          <AIInterpretationDisplay
                            interpretation={aiText}
                            data={aiData}
                            isLoading={aiMutation.isPending}
                            error={aiError}
                            className="mt-2"
                          />
                        </div>
                      );
                    })()}
                  </section>
                </div>
              );
            })()}
          </div>
        </div>
      </main>

      <FooterB />
    </div>
  );
}

export default AdaptationOptionsPage;
